import { createRoute, redirect, stripSearchParams } from '@tanstack/react-router';
import { Suspense } from 'react';
import { z } from 'zod';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { BusinessAddress } from '@Pages/happyFlowPages/businessAddress/BusinessAddress';
import { RegisteredAddress } from '@Pages/happyFlowPages/registeredAddress/RegisteredAddress';
import { beforeLoadGeneral } from '@Router/middleware';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { useBoundStore } from '@Store/useBoundStore';
import {
  getIndustryListQueryOptions,
  getNaicsListInfiniteQueryOptions,
} from '@Pages/happyFlowPages/industry/api/industry.api';

import { GeneralSpinner } from '@Components/generalSpinner';
import { MailingAddress } from '@Pages/happyFlowPages/mailingAddress/MailingAddress';
import { WhatDoYouSell } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/what-do-you-sell/WhatDoYouSell';
import { ToWhoYouSell } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/to-who-you-sell/ToWhoYouSell';
import { WhyDidYouChoose } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/why-did-you-hoose/WhyDidYouChoose';
import { CountriesBusinessWith } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/countries-business-with/CountriesBusinessWith';
import { MonthlyPaymentVolume } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/monthly-payment-volume/MonthlyPaymentVolume';
import { MultiBusinessIntro } from '@Pages/multiBusiness/MultiBusinessIntro';
import { fetchCountries } from '@Hooks/api/fetchCountries.api';
import { MAIN_INCOME_SOURCE } from '@Types/consts';
import {
  BUSINESS_MAIN_TYPES,
  type BusinessMainTypes,
} from '@Pages/happyFlowPages/businessTypeSelection/consts';
import {
  getMainBusinessTypeFromIncomeSource,
  isMainBusinessType,
} from '@Pages/happyFlowPages/businessTypeSelection/utils';
import { KnowYourBusinessPage, BusinessTypeSelectionPage, IndustryPage, IndustryOldPage } from '..';
import { rootRoute } from './rootRoute';
const getObData = useBoundStore.getState().onboardingSlice.getOnboardingData;

const BUSINESS_FLOW_STARTING_STEP = RoutesMap.BUSINESS_TYPE_SELECTION;

export const handleBusinessTypeRedirect = (search: {
  mainBusinessType?: BusinessMainTypes | null;
  [key: string]: unknown;
}) => {
  const { editMode } = useBoundStore.getState().onboardingSlice;
  if (editMode) return;

  const {
    customerData: {
      customerBusinessData: { mainIncomeSource },
    },
  } = useBoundStore.getState().customer;
  if (!search.mainBusinessType && mainIncomeSource && !isMainBusinessType(mainIncomeSource)) {
    const mainType = getMainBusinessTypeFromIncomeSource(mainIncomeSource);
    if (!mainType) return;
    throw redirect({
      to: routesModel[RoutesMap.BUSINESS_TYPE_SELECTION].path,
      search: {
        mainBusinessType: mainType,
      },
    });
  }
};

const KnowYourBusinessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.KNOW_YOUR_BUSINESS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.KNOW_YOUR_BUSINESS,
      shouldUpdateServerNavStack: true,
      stepToReturn: BUSINESS_FLOW_STARTING_STEP,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <KnowYourBusinessPage />
    </Suspense>
  ),
});

const businessTypeSelectionQueryParamsSchema = z.object({
  mainBusinessType: fallback(z.nativeEnum(BUSINESS_MAIN_TYPES).nullable(), null).default(null),
});
const BusinessTypeSelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.BUSINESS_TYPE_SELECTION].path,
  beforeLoad: ({ search }) => {
    beforeLoadGeneral({
      force: !getObData().editMode,
      page: RoutesMap.BUSINESS_TYPE_SELECTION,
      shouldUpdateServerNavStack: true,
    });
    handleBusinessTypeRedirect(search);
  },
  validateSearch: zodValidator(businessTypeSelectionQueryParamsSchema),
  search: {
    middlewares: [stripSearchParams({ mainBusinessType: null })],
  },
  component: BusinessTypeSelectionPage,
  wrapInSuspense: true,
  pendingComponent: GeneralSpinner,
});

const RegisteredAddressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.REGISTERED_ADDRESS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.REGISTERED_ADDRESS,
      shouldUpdateServerNavStack: true,
      stepToReturn: BUSINESS_FLOW_STARTING_STEP,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <RegisteredAddress />
    </Suspense>
  ),
});

const BusinessAddressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.BUSINESS_ADDRESS].path,
  beforeLoad: async ({ context }) => {
    beforeLoadGeneral({
      page: RoutesMap.BUSINESS_ADDRESS,
      shouldUpdateServerNavStack: true,
      stepToReturn: BUSINESS_FLOW_STARTING_STEP,
    });
    await context.queryClient.prefetchQuery({
      queryKey: ['countries', true, false],
      queryFn: () => fetchCountries(true, false),
      staleTime: Infinity,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <BusinessAddress />
    </Suspense>
  ),
});
const MalingAddressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.MAILING_ADDRESS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.MAILING_ADDRESS,
      shouldUpdateServerNavStack: true,
      stepToReturn: BUSINESS_FLOW_STARTING_STEP,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <MailingAddress />
    </Suspense>
  ),
});

const IndustryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.INDUSTRY].path,
  beforeLoad: async () => {
    beforeLoadGeneral({
      page: RoutesMap.INDUSTRY,
      shouldUpdateServerNavStack: true,
      stepToReturn: BUSINESS_FLOW_STARTING_STEP,
    });

    const {
      customer: { customerId },
      onboardingSlice: { getIsEmbedded },
    } = useBoundStore.getState();

    const isEmbedded = getIsEmbedded();

    return {
      getIndustryListQueryData: getIndustryListQueryOptions({
        customerId: customerId!,
        isEmbedded,
      }),
      getNaicsListQueryData: getNaicsListInfiniteQueryOptions({
        customerId: customerId!,
        isEmbedded,
        searchText: '',
      }),
    };
  },
  loader: async ({ context }) => {
    const {
      customer: {
        customerData: {
          customerBusinessData: { mainIncomeSource },
        },
      },
    } = useBoundStore.getState();

    if (mainIncomeSource === MAIN_INCOME_SOURCE.NON_PROFIT) {
      await context.queryClient.ensureQueryData(context.getIndustryListQueryData);
    } else {
      await context.queryClient.prefetchInfiniteQuery(context.getNaicsListQueryData);
    }
  },
  component: function IndustryComponent() {
    const incomeSource =
      useBoundStore.getState().customer.customerData.customerBusinessData.mainIncomeSource;

    return incomeSource === MAIN_INCOME_SOURCE.NON_PROFIT ? <IndustryOldPage /> : <IndustryPage />;
  },
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});
const EddEmbeddedWhatDoYouSellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_EMBEDDED_WHAT_DO_YOU_SELL].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_EMBEDDED_WHAT_DO_YOU_SELL,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <WhatDoYouSell />
    </Suspense>
  ),
});
const EddEmbeddedToWhoYouSellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_EMBEDDED_TO_WHO_YOU_SELL].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_EMBEDDED_TO_WHO_YOU_SELL,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <ToWhoYouSell />
    </Suspense>
  ),
});
const EddEmbeddedWhyDidYouChooseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_EMBEDDED_WHY_DID_YOU_CHOOSE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_EMBEDDED_WHY_DID_YOU_CHOOSE,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <WhyDidYouChoose />
    </Suspense>
  ),
});
const EddEmbeddedBusinessCountriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_EMBEDDED_BUSINESS_COUNTRIES].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_EMBEDDED_BUSINESS_COUNTRIES,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <CountriesBusinessWith />
    </Suspense>
  ),
});
const EddEmbeddedMonthlyPaymentVolumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_EMBEDDED_MONTHLY_PAYMENT_VOLUME].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_EMBEDDED_MONTHLY_PAYMENT_VOLUME,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <MonthlyPaymentVolume />
    </Suspense>
  ),
});

const MultiBusinessOnboardingIntroRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.MULTI_BUSINESS_ONBOARDING_INTRO].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.MULTI_BUSINESS_ONBOARDING_INTRO,
      shouldUpdateServerNavStack: false,
      stepToReturn: BUSINESS_FLOW_STARTING_STEP,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <MultiBusinessIntro />
    </Suspense>
  ),
});

export const businessRoutes = [
  IndustryRoute,
  KnowYourBusinessRoute,
  BusinessTypeSelectionRoute,
  RegisteredAddressRoute,
  BusinessAddressRoute,
  MalingAddressRoute,
  EddEmbeddedWhatDoYouSellRoute,
  EddEmbeddedToWhoYouSellRoute,
  EddEmbeddedWhyDidYouChooseRoute,
  EddEmbeddedBusinessCountriesRoute,
  EddEmbeddedMonthlyPaymentVolumeRoute,
  MultiBusinessOnboardingIntroRoute,
];
